<template>
    <div>
        <div class="">

            <div class="px-3 pt-4 pb-3">

                <div class="mb-3">
                    <div class="d-flex justify-content-between align-items-start">
                        <h4 class="mb-0">{{ resource.name }}</h4>
                        <div v-if="canEdit" class="">
                            <b-badge 
                                :variant="statusBadgeProps.variant"
                                class="text-uppercase font-size-90"
                            >
                                {{statusBadgeProps.text}}
                            </b-badge>
                        </div>
                    </div>
                </div>

                <!-- <hr v-if="isWideLayout"> -->

                <div class="row">

                    <div class="" :class="isWideLayout ? 'col-5' : 'col-12'">
                        <p class="mb-0" :class="isWideLayout ? '' : ''">{{ resource.description }}</p>
                        <DocHubVersionText v-if="hasVersion && isWideLayout" :version="version" class="mt-4"></DocHubVersionText>
                    </div>

                    <div class="" :class="isWideLayout ? 'col-7 my-0' : 'col-12 my-3'">
                        <!-- CONTENT AREA -->
                        <div v-if="hasFiles && !isVideo" class="">
                            <!-- <h6>Downloads</h6> -->
                            <div class="d-flex flex-column justify-content-around gap-2">
                                <base-s-3-file-download-button
                                    v-for="file in filesList"
                                    :key="file.id"
                                    :fileData="file"
                                    buttonVariant="subtle-primary"
                                ></base-s-3-file-download-button>
                            </div>
                            <div 
                                v-if="showDownloadBundleButton"
                                class="mt-2 text-right"
                            >
                                <file-download-bundle-button
                                    endpoint="dochubResourceVersion.get_file_bundle_url"
                                    :endpointArgs="version.id"
                                    buttonSize="md"
                                    buttonVariant="minimal-primary"
                                ></file-download-bundle-button>
                            </div>
                        </div>

                        <div v-if="isVideo" class="">
                            <b-button
                                v-if="activeVersionVideoUrl"
                                variant="sharp-primary"
                                block
                                size="lg"
                                @click="showVideoModal"
                            >
                                <b-icon icon="play-circle-fill" class="mr-2" variant="primary"></b-icon>
                                Watch Video
                            </b-button>
                        </div>

                        <div v-if="!hasVersion && canEdit" key="noVersionCanEdit" class="">
                            <b-button variant="primary" block size="lg" @click="startNewVersion">
                                Add Files
                            </b-button>
                        </div>
                        
                    </div>
                
                </div>
                
                <DocHubVersionText v-if="hasVersion && !isWideLayout" :version="version" class=""></DocHubVersionText>

            </div>
            
            <div 
                v-if="canEdit"
                class="d-flex flex-wrap gap-1 w-100 p-2 bg-gray-200"
                :class="isWideLayout ? 'justify-content-end' : 'justify-content-around'"
            >
                <b-button
                    variant="minimal-primary"
                    size="sm"
                    class="special-text"
                    @click="requestEditResource"
                >
                    Edit
                </b-button>

                <b-button
                    v-if="hasVersion"
                    variant="minimal-primary"
                    size="sm"
                    class="special-text"
                    @click="startNewVersion"
                >
                    Create New Version
                </b-button>

                <!-- not allowing this for now -->
                <!-- <b-button
                    v-if="hasVersion"
                    variant="minimal-primary"
                    size="sm"
                    class="special-text"
                    @click="startUpdateVersion"
                >
                    Update Existing Version
                </b-button> -->

                <b-button
                    v-if="resource.can_go_draft"
                    variant="minimal-secondary"
                    size="sm"
                    class="special-text"
                    @click="changeStatus('draft')"
                >
                    Change to Draft
                </b-button>
                <b-button
                    v-if="resource.can_go_published"
                    variant="success"
                    size="sm"
                    class="special-text"
                    @click="changeStatus('published')"
                >
                    Publish
                </b-button>
                <b-button
                    v-if="resource.can_go_archived"
                    variant="minimal-danger"
                    size="sm"
                    class="special-text"
                    @click="changeStatus('archived')"
                >
                    Archive
                </b-button>
            </div>

            <b-modal
                v-if="canEdit && !isVideo"
                v-model="editFormShowing"
                :title="isUpdate ? 'Update this version' : 'Create a new version'"
                size="md"
                hide-footer
                @hidden="onCloseEditModal"
            >
                <doc-hub-resource-version-form
                    :resourceId="resource.id"
                    :versionData="formVersionData"
                    @cancel="onEditCancel"
                    @saved="onEditSaved"
                ></doc-hub-resource-version-form>
            </b-modal>

            <b-modal
                v-if="activeVersionVideoUrl"
                v-model="videoModalShowing"
                :title="resource.name"
                size="xxl"
                body-class="p-0"
                centered
                hide-footer
            >
                <div style="position: relative; padding-top: 56.25%;">
                    <iframe
                        :src="activeVersionVideoUrl"
                        loading="lazy"
                        referrerpolicy="origin"
                        style="border: none; position: absolute; top: 0; left: 0; height: 100%; width: 100%;"
                        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                        allowfullscreen="true"
                    ></iframe>  <!-- need referrerpolicy="origin" at least, if you are restricting videos by origin -->
                </div>
            </b-modal>

        </div>
    </div>
</template>

<script>
import { 
    errorHandling,
    currentUserInfoMixin,
} from '../../js/custom-vue-mixins';
import { suppliosApi } from '../../js/api-service';
import { FormatDateHelper } from '../../js/filters-helpers';
import DocHubResourceVersionForm from './DocHubResourceVersionForm.vue';
import DocHubVersionText from './DocHubVersionText.vue';
import BaseS3FileDownloadButton from '../s3_files/BaseS3FileDownloadButton.vue';
import cloneDeep from 'lodash/cloneDeep';
import FileDownloadBundleButton from '../s3_files/FileDownloadBundleButton.vue';

export default {
    name: 'DocHubResourceItem',
    components: {
        DocHubResourceVersionForm,
        BaseS3FileDownloadButton,
        FileDownloadBundleButton,
        DocHubVersionText,
    },
    mixins: [
        errorHandling,
        currentUserInfoMixin,
    ],
    props: {
        resource: {
            type: Object,
            required: true,
        },
        allowEdit: {  // must set this true if edit is allowed at all (and it only shows edit stuff if user can do it)
            type: Boolean,
            default: false,
        },
        layoutType: {
            type: String,
            default: 'LIST',  // options are from backend enum: GRID, LIST - this controls things NOT in groups, groups have their own layout
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,
            editFormShowing: false,
            isUpdate: false,  
            formVersionData: null,  // set it based on whether doing a create/update 
            videoModalShowing: false,
        }
    },
    computed: {
        version() {
            // pull out the version from 'active_version' to make it easier to work with
            return this.resource.active_version || {};
        },
        hasVersion() {
            return !!this.version?.id;
        },
        isVideo() {
            return !!this.version.cloudflare_video_url;
        },
        activeVersionVideoUrl() {
            return this.version.cloudflare_video_url || '';
        },
        filesList() {
            // pull out from version, to make it easier to work with and provide a default
            return this.version.files_list || [];
        },
        hasFiles() {
            return !!this.filesList && Array.isArray(this.filesList) && this.filesList.length;
        },
        showDownloadBundleButton() {
            return !!this.version.id && this.filesList.length > 1;
        },
        canEdit() {
            return this.allowEdit && this.$currentUserInfo.is_tenant_admin;
        },
        statusBadgeProps() {
            // return things for showing status badge
            const text = this.resource.status;
            let variant = 'secondary';
            switch (this.resource.status) {
                case 'draft':
                    variant = 'secondary';
                    break;
                case 'published': 
                    variant = 'success';
                    break;
                case 'archived':
                    variant = 'danger';
                    break;
                default:
                    break;
            }
            return { text, variant }
        },
        isWideLayout() {
            // could be the LIST layout, or the FULL_CARDS layout
            return ['LIST', 'FULL_CARDS'].includes(this.layoutType);
        },
        isSmallCardLayout() {
            return ['SMALL_CARDS', 'GRID'].includes(this.layoutType);
        },
        isLargeCardLayout() {
            return this.layoutType === 'LARGE_CARDS';
        },
    },
    mounted() {
        
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        startNewVersion() {
            this.formVersionData = {};
            this.isUpdate = false;
            this.editFormShowing = true;
        },
        startUpdateVersion() {
            this.formVersionData = cloneDeep(this.version);
            this.isUpdate = true;
            this.editFormShowing = true;
        },
        onEditCancel() {
            this.onCloseEditModal();
            // no need to refresh from parent here
        },
        onEditSaved() {
            this.onCloseEditModal();
            this.refresh();
        },
        onCloseEditModal() {
            // RESET THINGS
            this.isUpdate = false;
            this.formVersionData = null;
            this.editFormShowing = false;
        },
        requestEditResource() {
            // for some reason, we'll have parent component update the overall resource
            this.$emit('request-update', this.resource.id);
        },
        formatDate(dateString) {
            return FormatDateHelper(dateString);
        },
        refresh() {
            // for now just reload the entire list
            this.$emit('updated');
        },
        async changeStatus(newStatus) {
            this.loading = true;
            const postData = {status: newStatus};
            try {
                const resp = await suppliosApi.dochubResource.change_status(this.resource.id, postData);
                console.log('updated status', resp.data);
                this.$toast('Updated Status');
                this.refresh();
            } catch (error) {
                this.apiErrorHandler(error);
            }
            this.loading = false;
        },
        showVideoModal() {
            if (!this.activeVersionVideoUrl) {
                return;
            }
            this.videoModalShowing = true;
        },
    }
}
</script>

<style>

</style>